import type { ReactNode } from 'react'
import { cn } from './cn'

type AlertVariant = 'error' | 'success' | 'warning' | 'info'

const variants: Record<AlertVariant, { bg: string; text: string }> = {
  error: { bg: 'bg-err-bg border-line', text: 'text-err-ink' },
  success: { bg: 'bg-ok-bg border-line', text: 'text-ok-ink' },
  warning: { bg: 'bg-warn-bg border-line', text: 'text-warn-ink' },
  info: { bg: 'bg-info-bg border-line', text: 'text-info-ink' },
}

export interface AlertProps {
  variant?: AlertVariant
  children?: ReactNode
  onDismiss?: () => void
  className?: string
  /** Optional id so callers can associate the alert via aria-describedby. */
  id?: string
}

export function Alert({ variant = 'error', children, onDismiss, className = '', id }: AlertProps) {
  const style = variants[variant] || variants.error
  const role = variant === 'error' || variant === 'warning' ? 'alert' : 'status'
  return (
    <div id={id} className={cn('flex items-start gap-2 px-4 py-3 rounded-control border', style.bg, className)} role={role}>
      <div className={cn('text-sm flex-1', style.text)}>{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className={cn('inline-flex items-center justify-center p-1.5 -m-1 shrink-0 rounded-control coarse:min-h-tap coarse:min-w-tap hover:opacity-70 focus-visible:outline-none focus-visible:shadow-focus', style.text)}
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}
